import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { getCountryName } from '@commons/utils/countryCode';
import { Label } from '@commons/utils/styledLibraryComponents';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { Textarea } from '@lib/inpulse/Textarea';

import theme from '@theme';

import {
  Block,
  CommentBlock,
  Divider,
  PanelContainer,
  PanelTitle,
  PanelTitleIcon,
} from './styledComponents';

const EXTERNAL_REFERENCE_BADGE_WIDTH = 'fit-content';

/**
 * Side panel of the order form: who the order is for, and the free comment carried by the order
 * itself. The client information is read-only here — it is administered from the client detail page
 * — and the contact emails are deliberately left out.
 */
const OrderClientPanel = (props) => {
  const { externalClient, comment, isCommentReadOnly, onChangeComment } = props;

  const { contact, telephone, address, postCode, city, country } = externalClient.company ?? {};

  const cityLine = [postCode, city].filter((value) => !!value).join(' ');

  const addressLines = [address, cityLine, getCountryName(country)].filter((value) => !!value);

  // Keyed on the index: two lines can legitimately hold the same text (a city equal to the country).
  const renderTextLine = (value, index) => (
    <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16} key={index}>
      {value}
    </Text>
  );

  const renderBlock = (title, lines) => (
    <Block>
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
        {title}
      </Text>
      {lines.length ? lines.map(renderTextLine) : renderTextLine('-')}
    </Block>
  );

  return (
    <PanelContainer>
      <Block>
        <PanelTitle>
          <PanelTitleIcon src={'/images/inpulse/briefcase-black-small.svg'} />
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_BOLD_HEIGHT_16}>
            {externalClient.name}
          </Text>
        </PanelTitle>
        {!!externalClient.externalReference && (
          <Label
            background={theme.colors.greys.light}
            color={theme.colors.greys.darkest}
            type={'plain'}
            width={EXTERNAL_REFERENCE_BADGE_WIDTH}
          >
            {externalClient.externalReference}
          </Label>
        )}
      </Block>

      {renderBlock(
        i18next.t('EXTERNAL_CLIENTS.CREATION.CONTACT'),
        [contact, telephone].filter((value) => !!value),
      )}

      {renderBlock(i18next.t('EXTERNAL_CLIENTS.CREATION.ADDRESS'), addressLines)}

      <Divider />

      <CommentBlock>
        <PanelTitle>
          <PanelTitleIcon src={'/images/inpulse/edit-pen-black-small.svg'} />
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
            {i18next.t('EXTERNAL_CLIENTS.CREATION.COMMENTS')}
          </Text>
        </PanelTitle>
        <Textarea
          placeholder={i18next.t('EXTERNAL_ORDERS.FORM.COMMENT_PLACEHOLDER')}
          readOnly={isCommentReadOnly}
          value={comment == null ? '' : comment}
          onChange={(event) => onChangeComment(event.target.value)}
        />
      </CommentBlock>
    </PanelContainer>
  );
};

OrderClientPanel.propTypes = {
  externalClient: PropTypes.shape({
    name: PropTypes.string,
    externalReference: PropTypes.string,
    company: PropTypes.object,
  }).isRequired,
  comment: PropTypes.string,
  isCommentReadOnly: PropTypes.bool.isRequired,
  onChangeComment: PropTypes.func.isRequired,
};

OrderClientPanel.defaultProps = {
  comment: '',
};

export default OrderClientPanel;
